import * as React from 'react';

import { cn } from '@/lib/utils';

export interface TextareaProps extends Omit<React.ComponentProps<'textarea'>, 'size'> {
  state?: 'default' | 'error';
  /**
   * Mirrors Input's size scale so a description sits visually next to its
   * single-line siblings without a height jump:
   *   - `md` (default) — generous py-4 for auth/marketing surfaces
   *   - `sm` — dense admin-form field (py-3)
   */
  size?: 'md' | 'sm';
}

const shellBase =
  'flex w-full rounded-[10px] border bg-surface px-4 transition-[border,box-shadow] disabled:cursor-not-allowed disabled:opacity-50';

const shellSize: Record<NonNullable<TextareaProps['size']>, string> = {
  md: 'py-4',
  // Mirrors the Input sm change — keeps a multi-line field's chrome on the
  // same vertical rhythm as a single-line one stacked above or below it.
  sm: 'py-2.5',
};

const shellDefault =
  'border-primary-100 focus-within:border-primary-300 focus-within:shadow-(--shadow-focus-ring)';

const shellError =
  'border-error-300 focus-within:border-error-500 focus-within:shadow-(--shadow-focus-ring-error)';

const fieldBase =
  'w-full flex-1 resize-y bg-transparent font-sans text-body-md leading-relaxed outline-none';

const fieldSize: Record<NonNullable<TextareaProps['size']>, string> = {
  md: 'font-light min-h-28',
  sm: 'font-normal min-h-24',
};

const fieldDefault = 'text-accent-ink placeholder:text-neutral-500';
const fieldError = 'text-error-500 placeholder:text-error-500';

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, state, size = 'md', ...props }, ref) => {
    const isError =
      state === 'error' || props['aria-invalid'] === true || props['aria-invalid'] === 'true';
    return (
      <div className={cn(shellBase, shellSize[size], isError ? shellError : shellDefault)}>
        <textarea
          ref={ref}
          className={cn(fieldBase, fieldSize[size], isError ? fieldError : fieldDefault, className)}
          {...props}
        />
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
